import React from "react";
import { Modal, Pressable, View, StyleSheet } from "react-native";
import CCText from "@/Atoms/CCText";
import { useMediaQuery } from "@/Hooks/useMediaQuery/useMediaQuery";

// Filter options mirror the design prototype (Rating band + Activity), each
// single-select. These are UI filters — the directory endpoint currently only
// supports name search, so selections are surfaced but not yet sent to the API.
export const RATING_BANDS = ["<1000", "1000-1400", "1400+"] as const;
export const ACTIVITY_LEVELS = ["Quiet", "Active", "Buzzing"] as const;

export type ClubFilters = { band: string | null; activity: string | null };

export function activeFilterList(f: ClubFilters): { key: keyof ClubFilters; label: string }[] {
  const out: { key: keyof ClubFilters; label: string }[] = [];
  if (f.band) out.push({ key: "band", label: f.band });
  if (f.activity) out.push({ key: "activity", label: f.activity });
  return out;
}

function Chip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={[styles.chip, active ? styles.chipActive : styles.chipIdle]}
    >
      <CCText style={{ fontSize: 13, fontWeight: "600", color: active ? "#fff" : "#4D3F37" }}>
        {label}
      </CCText>
    </Pressable>
  );
}

export default function FilterSheet({
  visible,
  filters,
  count,
  onChange,
  onClear,
  onClose,
}: {
  visible: boolean;
  filters: ClubFilters;
  count: number;
  onChange: (f: ClubFilters) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const { isMobileOrTab } = useMediaQuery();
  const isWide = !isMobileOrTab;

  const toggle = (key: keyof ClubFilters, value: string) =>
    onChange({ ...filters, [key]: filters[key] === value ? null : value });

  return (
    <Modal visible={visible} transparent animationType={isWide ? "fade" : "slide"} onRequestClose={onClose}>
      <Pressable style={[styles.backdrop, isWide ? styles.backdropCenter : styles.backdropBottom]} onPress={onClose}>
        <Pressable
          style={[styles.sheet, isWide ? styles.sheetCard : styles.sheetBottom]}
          onPress={() => {}}
        >
          {!isWide ? <View style={styles.grabber} /> : null}
          <CCText fontWeight="thunder" style={styles.title}>Filter clubs</CCText>

          <CCText style={styles.groupLabel}>Rating band</CCText>
          <View style={styles.chipRow}>
            {RATING_BANDS.map((b) => (
              <Chip key={b} label={b} active={filters.band === b} onPress={() => toggle("band", b)} />
            ))}
          </View>

          <CCText style={[styles.groupLabel, { marginTop: 18 }]}>Activity</CCText>
          <View style={styles.chipRow}>
            {ACTIVITY_LEVELS.map((a) => (
              <Chip key={a} label={a} active={filters.activity === a} onPress={() => toggle("activity", a)} />
            ))}
          </View>

          <View style={styles.actions}>
            <Pressable testID="filter-clear" style={styles.clearBtn} onPress={onClear}>
              <CCText style={{ fontSize: 15, fontWeight: "600", color: "#4D3F37" }}>Clear all</CCText>
            </Pressable>
            <Pressable testID="filter-show" style={styles.showBtn} onPress={onClose}>
              <CCText style={{ fontSize: 15, fontWeight: "600", color: "#fff" }}>Show {count}</CCText>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: "rgba(0,0,0,0.4)" },
  backdropBottom: { justifyContent: "flex-end" },
  backdropCenter: { justifyContent: "center", alignItems: "center", padding: 28 },
  sheet: { backgroundColor: "#FAF6EB", padding: 20, paddingBottom: 26 },
  sheetBottom: { borderTopLeftRadius: 24, borderTopRightRadius: 24, width: "100%" },
  sheetCard: { borderRadius: 24, width: "100%", maxWidth: 420 },
  grabber: {
    width: 40,
    height: 4,
    borderRadius: 999,
    backgroundColor: "#D8CDB4",
    alignSelf: "center",
    marginBottom: 16,
  },
  title: { fontSize: 22, color: "#262322" },
  groupLabel: {
    marginTop: 16,
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0.04,
    textTransform: "uppercase",
    color: "#888280",
  },
  chipRow: { flexDirection: "row", gap: 8, flexWrap: "wrap", marginTop: 9 },
  chip: {
    paddingVertical: 8,
    paddingHorizontal: 13,
    borderRadius: 999,
    borderWidth: 1.5,
  },
  chipIdle: { backgroundColor: "#fff", borderColor: "rgba(38,35,34,0.20)" },
  chipActive: { backgroundColor: "#3DAB9E", borderColor: "#3DAB9E" },
  actions: { flexDirection: "row", gap: 10, marginTop: 22 },
  clearBtn: {
    flex: 1,
    backgroundColor: "#fff",
    borderWidth: 1.5,
    borderColor: "#4D3F37",
    borderRadius: 8,
    paddingVertical: 13,
    alignItems: "center",
  },
  showBtn: {
    flex: 1,
    backgroundColor: "#3DAB9E",
    borderRadius: 32,
    paddingVertical: 13,
    alignItems: "center",
  },
});
